import {
  CovIcon,
  CovPagination,
  IconName,
} from "@cx-component-library/react-components";
import { Box, Grid, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import type { HelpCenterResult } from "hooks/help-center";
import type { HelpSearchData } from "hooks/help-search";
import { useEffect, useState } from "react";
import type { TFunction } from "react-i18next";
import type { SetURLSearchParams } from "react-router-dom";
import { HelpCard } from "./HelpCollapseContent";
import type { Item } from "./HelpConstant";

interface Props {
  helpCenterApiData: HelpCenterResult | undefined;
  targetItem: Item | undefined;
  setSearchParams: SetURLSearchParams;
  searchData: HelpSearchData[] | undefined;
  handleReadMore: (item: HelpSearchData) => void;
  t: TFunction;
}

const HelpPageContent = ({
  helpCenterApiData,
  targetItem,
  setSearchParams,
  searchData,
  handleReadMore,
  t,
}: Props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [searchData]);

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const paginatedData = searchData
    ? searchData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : [];

  return searchData ? (
    <div style={{ width: "100%" }}>
      {paginatedData.map((i) => (
        <Box key={i.id} className="help-content-container">
          <div>
            <Typography className="help-content-title">
              {i.displayName}
            </Typography>
            <div
              className="help-content-body"
              dangerouslySetInnerHTML={{ __html: i?.searchTextRelated ?? "" }}
            />
            <Box
              className="help-read-more"
              onClick={() => handleReadMore(i)}
              style={{ cursor: "pointer" }}
            >
              <Typography className="help-read-more-text">
                {t("ldp_read_more")}
              </Typography>
              <CovIcon
                iconName={IconName.CHEVRON_RIGHT}
                iconColor="#009FE4"
                iconSize="xs"
              />
            </Box>
          </div>
          <Box className="help-content-image">
            <img src={i.imageUrl} alt="Imagio helps" />
          </Box>
        </Box>
      ))}
      <CovPagination
        count={searchData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  ) : (
    <>
      <Grid size={{ xs: 3, lg: 3, xxl: 3 }} marginBottom={"24px"}>
        {/** biome-ignore lint/correctness/noUnsafeOptionalChaining: ToBeEvaluated with the Team */}
        {(helpCenterApiData?.data.length ? helpCenterApiData?.data : []).map(
          (help: any) => (
            <HelpCard
              key={help.id}
              helpId={help.id}
              title={help.displayName}
              sections={help.items}
              targetItem={targetItem}
              setSearchParams={setSearchParams}
            />
          ),
        )}
      </Grid>
      <Grid size={{ xs: 9, lg: 9, xxl: 9 }} marginBottom={"24px"}>
        <Box key={targetItem?.id} className="help-detail-container">
          <Box className="help-detail-content">
            <div
              dangerouslySetInnerHTML={{ __html: targetItem?.text ?? "" }}
            ></div>
          </Box>
        </Box>
      </Grid>
    </>
  );
};

export default HelpPageContent;
